import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {useMenu} from 'extensions/viewport/useMenu';
import {useCopyLink} from 'interface/hooks/useCopyLink';
import {useModalShare} from 'interface/hooks/useModalShare';
import {useModalTrash} from 'interface/hooks/useModalTrash';
import {useModalPurge} from 'interface/hooks/useModalPurge';
import {useModalRename} from 'interface/hooks/useModalRename';
import {useModalDescribe} from 'interface/hooks/useModalDescribe';
import {useModalPassword} from 'interface/hooks/useModalPassword';
import {useModalFileDrop} from 'interface/hooks/useModalFileDrop';
import {useModalBulkDownload} from 'interface/hooks/useModalBulkDownload';
import {useModalUpsellFeature} from 'interface/hooks/useModalUpsellFeature';
import {isQuickkey, isFolderkey} from 'utils/mediafire';
import {isNative, getHost} from 'utils/platform';
import {vectors, colors} from 'config/styles';
import {testBit} from 'utils/common/data';

import * as effects from 'interface/hooks/effects/files';
import {FilesItem, FilesItemFlag} from 'store/slices/files/types';

export function useMenuFiles(
  ids: string[],
  item: FilesItem,
  options: {
    isPremium: boolean,
    hasPrivate: boolean,
    primaryColor: string,
  },
) {
  const {t} = useLingui();
  const files = ids.filter(id => isQuickkey(id));
  const folders = ids.filter(id => isFolderkey(id));

  const hasMultiple = ids.length > 1;
  const hasFolders = folders.length > 0;
  const hasFiles = files.length > 0;
  const hasMixed = hasFiles && hasFolders;
  const hasOnlyFiles = hasFiles && !hasFolders;
  const hasOnlyFolders = hasFolders && !hasFiles;

  const isOwned = testBit(item?.flag, FilesItemFlag.isOwned);
  const isDeleted = item?.deleted;
  const isInAccount = !isDeleted && isOwned;
  const isFolder = item?.type === 'folder';
  const isImage = item?.type === 'image';
  const isPrivate = (options.hasPrivate || item?.private || item?.shareLinkEnabled === false);

  const hasDistributeAccess = !isPrivate || isOwned;

  const hasView = isImage && !isDeleted && !hasMultiple;
  const hasShare = !isDeleted && (!hasMixed || isNative()) && hasDistributeAccess;
  const hasOneTimeLink = !isImage && !isDeleted && !hasMultiple && hasOnlyFiles && hasDistributeAccess;
  const hasCopyLink = !isDeleted && !isPrivate && hasDistributeAccess;
  const hasDownload = !isDeleted && !hasMultiple && !hasOnlyFolders;
  const hasDownloadBulk = !isDeleted && (hasMultiple || hasOnlyFolders);
  const hasSaveToMyFiles = !isOwned;
  const hasCopyTo = isInAccount;
  const hasMoveTo = isInAccount;
  const hasRename = isInAccount && !hasMultiple;
  const hasDescription = isInAccount && !hasMultiple;
  const hasPasswordProtect = isInAccount && hasOnlyFiles && !hasMultiple && !isImage;
  const hasFileDropConfigure = isInAccount && hasOnlyFolders && !hasMultiple;
  const hasMoveToTrash = isInAccount;
  const hasRestore = isDeleted;
  const hasPurge = isDeleted;

  const url = hasMultiple ? `${getHost()}/folder/${ids.join(',')}/shared` : item?.url;
  const name = hasMultiple ? 'Multiple Items' : item?.name;
  const title = hasMultiple ? t`${ids.length} items selected` : item?.name;
  const destination = 'myfiles';

  const icon = useCallback((viewbox: string, width: number, height: number, icon: string) =>
    <Svg viewBox={viewbox} width={width} height={height}>
      <Path d={icon} fill={colors.neutral.mid}/>
    </Svg>
  , []);

  const dispatch = useDispatch();

  const save = useCallback(effects.pick(dispatch, {action: 'save', destination, ids}), [ids]);
  const copy = useCallback(effects.pick(dispatch, {action: 'copy', destination, ids}), [ids]);
  const move = useCallback(effects.pick(dispatch, {action: 'move', destination, ids}), [ids]);
  const restore = useCallback(effects.pick(dispatch, {action: 'restore', destination, ids}), [ids]);
  const download = useCallback(effects.downloadFile(item?.url, true), [item]);
  const viewImage = useCallback(effects.downloadFile(item?.url), [item]);
  const copyLink = useCopyLink(ids, item?.url);
  
  const modalTrash = useModalTrash(ids);
  const modalPurge = useModalPurge(ids);
  const modalRename = useModalRename(ids[0], item?.name);
  const modalDescribe = useModalDescribe(ids[0], item?.name, item?.description, isFolder);
  const modalPassword = useModalPassword(ids[0], item?.name, isFolder);
  const modalFileDrop = useModalFileDrop(ids[0], item?.name, item?.filedrop, options.primaryColor);
  const modalBulkDownload = useModalBulkDownload(ids);

  const [show1TDL] = useModalShare(ids, name, url, true, options.hasPrivate);
  const [showShare] = useModalShare(ids, name, url, undefined, options.hasPrivate);
  const [showUpsellOneTimeLink] = useModalUpsellFeature('pro', 'one_time_link');
  const [showUpsellFileDrop] = useModalUpsellFeature('pro', 'filedrop');
  const [showUpsellPassword] = useModalUpsellFeature('pro', 'password');
  const [showUpsellBulkDownload] = useModalUpsellFeature('pro', 'bulkdownload');

  const {open, exit, reference} = useMenu(() => [
    {
      command: 'header',
      text: title,
    },
    hasShare && {
      command: 'share',
      text: t`Share`,
      icon: icon('0 0 20 24', 18, 18, vectors.icons.share),
    },
    hasOneTimeLink && {
      command: 'one-time-link',
      text: t`1-Time Link`,
      icon: icon('0 0 24 24', 20, 20, vectors.icons.clock),
    },
    hasCopyLink && {
      command: 'copy-link',
      text: t`Copy Link`,
      icon: icon('0 0 20 10', 18, 18, vectors.icons.public),
    },
    hasView && {
      command: 'view',
      text: t`View Image`,
      icon: icon('0 0 16 20', 18, 18, vectors.icons.image),
    },
    hasDownload && {
      command: 'download',
      text: t`Download`,
      icon: icon('0 0 16 20', 18, 18, vectors.icons.download),
    },
    hasDownloadBulk && {
      command: 'bulk-download',
      text: t`Bulk Download`,
      icon: icon('0 0 16 20', 18, 18, vectors.icons.download),
    },
    hasSaveToMyFiles && {
      command: 'save',
      text: t`Save to My Files...`,
      icon: icon('0 0 60 48', 18, 18, vectors.icons.save),
    },
    isInAccount && {
      command: '',
      text: '-',
    },
    hasMoveTo && {
      command: 'move',
      text: t`Move to...`,
      icon: icon('0 0 24 24', 18, 18, vectors.icons.move),
    },
    hasCopyTo && {
      command: 'copy',
      text: t`Copy to...`,
      icon: icon('0 0 24 24', 18, 18, vectors.icons.copy),
    },
    hasRename && {
      command: 'rename',
      text: t`Rename`,
      icon: icon('0 0 24 20', 18, 18, vectors.icons.rename),
    },
    hasDescription && {
      command: 'describe',
      text: t`Description`,
      icon: icon('0 0 24 10', 18, 18, vectors.icons.description),
    },
    hasPasswordProtect && {
      command: 'password',
      text: t`Password Protect`,
      icon: icon('0 0 24 24', 18, 18, vectors.icons.password),
    },
    hasFileDropConfigure && {
      command: 'filedrop',
      text: t`Configure FileDrop`,
      icon: icon('0 0 24 16', 16, 18, vectors.icons.filedrop),
    },
    isInAccount && {
      command: '',
      text: '-',
    },
    hasMoveToTrash && {
      command: 'trash',
      text: t`Move to Trash`,
      icon: icon('0 0 16 22', 16, 18, vectors.icons.trash),
    },
    hasRestore && {
      command: 'restore',
      text: t`Restore to folder`,
      icon: icon('0 0 24 24', 20, 20, vectors.icons.restoreTrash),
    },
    hasPurge && {
      command: 'purge',
      text: t`Delete Permanently`,
      icon: icon('0 0 24 24', 20, 20, vectors.icons.emptyTrash),
    },
  ], (command) => {
    exit();
    switch (command) {
      case 'view':
        return viewImage({});
      case 'download':
        return download({});
      case 'copy-link':
        return copyLink({});
      case 'save':
        return save({});
      case 'move':
        return move();
      case 'copy':
        return copy();
      case 'restore':
        return restore();
      case 'trash':
        return modalTrash.open();
      case 'purge':
        return modalPurge.open();
      case 'rename':
        return modalRename.open();
      case 'describe':
        return modalDescribe.open();
      case 'share':
        return showShare();
      case 'bulk-download':
        return options.isPremium
          ? modalBulkDownload.open()
          : showUpsellBulkDownload();
      case 'password':
        return options.isPremium || item?.password
          ? modalPassword.open()
          : showUpsellPassword();
      case 'filedrop':
        return options.isPremium
          ? modalFileDrop.open()
          : showUpsellFileDrop();
      case 'one-time-link':
        return options.isPremium
          ? show1TDL()
          : showUpsellOneTimeLink();
    }
  }, hasMultiple);

  return {open, exit, reference};
}
